import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { exporterExcel } from '../services/excelService';
import api from '../services/api';

const C = {
  primary: '#2E7D32', primaryDark: '#1B5E20',
  border: '#E0E0E0', light: '#F4F6F9',
  green50: '#F1F8F2', green100: '#E8F5E9',
  white: '#FFFFFF', textDark: '#212121', textMid: '#616161', textLight: '#9E9E9E',
};

export default function ListeSaisiesPage() {
  const navigate = useNavigate();
  const { utilisateur } = useAuth();
  const estAdmin = utilisateur?.role === 'admin';
  const [saisies, setSaisies] = useState([]);
  const [abattoirs, setAbattoirs] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [message, setMessage] = useState({ type: '', texte: '' });
  const [hoveredRow, setHoveredRow] = useState(null);
  const today = new Date();
  const debutMois = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const finMois = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
  const [filtreAbattoir, setFiltreAbattoir] = useState('');
  const [filtreDebut, setFiltreDebut] = useState(debutMois);
  const [filtreFin, setFiltreFin] = useState(finMois);
  const [page, setPage] = useState(1);
  const [itemsParPage, setItemsParPage] = useState(10);

  const chargerSaisies = async () => {
    try {
      const r = await api.get('/saisies');
      setSaisies(r.data);
    } catch (err) {
      setMessage({ type: 'erreur', texte: 'Erreur lors du chargement.' });
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    chargerSaisies();
    api.get('/abattoirs').then(r => setAbattoirs(r.data));
  }, []);

  const valider = async (id) => {
    if (!window.confirm('Confirmer la validation ?')) return;
    try {
      await api.put(`/saisies/${id}/valider`);
      setMessage({ type: 'succes', texte: 'Journée validée avec succès !' });
      chargerSaisies();
    } catch (err) {
      setMessage({ type: 'erreur', texte: err.response?.data?.message || 'Erreur.' });
    }
  };

  const calcSaisie = (s) => {
    const bov_nbr = (s.bovins_brut||0)-(s.camelins||0)-(s.sorties_bovins||0);
    const bov_poids = (parseFloat(s.poids_bovins)||0)-(parseFloat(s.poids_camelins)||0)-(parseFloat(s.vf_bovins)||0);
    const ovi_nbr = (s.ovins||0)-(s.sorties_ovins||0);
    const ovi_poids = (parseFloat(s.poids_ovins)||0)-(parseFloat(s.vf_ovins)||0);
    const cap_nbr = (s.caprins||0)-(s.sorties_caprins||0);
    const cap_poids = (parseFloat(s.poids_caprins)||0)-(parseFloat(s.vf_caprins)||0);
    const por_nbr = (s.porcins||0)-(s.sorties_porcins||0);
    const por_poids = (parseFloat(s.poids_porcins)||0)-(parseFloat(s.vf_porcins)||0);
    const cam_poids = parseFloat(s.poids_camelins)||0;
    const vf_total = (parseFloat(s.vf_bovins)||0)+(parseFloat(s.vf_ovins)||0)+(parseFloat(s.vf_caprins)||0)+(parseFloat(s.vf_porcins)||0);
    const poids_total = bov_poids + ovi_poids + cap_poids + por_poids + cam_poids + vf_total;
    return { bov_nbr, bov_poids, ovi_nbr, ovi_poids, cap_nbr, cap_poids, por_nbr, por_poids, cam_poids, vf_total, poids_total };
  };

  const saisiesFiltrees = saisies.filter(s => {
    const date = new Date(s.date_saisie);
    const debut = filtreDebut ? new Date(filtreDebut) : null;
    const fin = filtreFin ? new Date(filtreFin) : null;
    if (debut && date < debut) return false;
    if (fin && date > fin) return false;
    if (filtreAbattoir && s.abattoir_id !== parseInt(filtreAbattoir)) return false;
    return true;
  });

  // Période N-1 pour comparaison
  const saisiesN1 = saisies.filter(s => {
    if (!filtreDebut || !filtreFin) return false;
    const date = new Date(s.date_saisie);
    const d1 = new Date(filtreDebut); d1.setFullYear(d1.getFullYear()-1);
    const d2 = new Date(filtreFin); d2.setFullYear(d2.getFullYear()-1);
    if (date < d1 || date > d2) return false;
    if (filtreAbattoir && s.abattoir_id !== parseInt(filtreAbattoir)) return false;
    return true;
  });

  const calcTotaux = (liste) => liste.reduce((acc, s) => {
    const c = calcSaisie(s);
    return {
      bov_nbr: acc.bov_nbr+c.bov_nbr, bov_poids: acc.bov_poids+c.bov_poids,
      cam_nbr: acc.cam_nbr+(s.camelins||0), cam_poids: acc.cam_poids+c.cam_poids,
      ovi_nbr: acc.ovi_nbr+c.ovi_nbr, ovi_poids: acc.ovi_poids+c.ovi_poids,
      cap_nbr: acc.cap_nbr+c.cap_nbr, cap_poids: acc.cap_poids+c.cap_poids,
      por_nbr: acc.por_nbr+c.por_nbr, por_poids: acc.por_poids+c.por_poids,
      poids_total: acc.poids_total+c.poids_total,
    };
  }, {bov_nbr:0,bov_poids:0,cam_nbr:0,cam_poids:0,ovi_nbr:0,ovi_poids:0,cap_nbr:0,cap_poids:0,por_nbr:0,por_poids:0,poids_total:0});

  const totaux = calcTotaux(saisiesFiltrees);
  const totauxN1 = calcTotaux(saisiesN1);

  const evol = (v, v1) => {
    if (!v1 || v1 === 0) return null;
    return ((v - v1) / v1 * 100).toFixed(1);
  };
  const evolColor = (pct) => pct === null ? C.textLight : parseFloat(pct) >= 0 ? C.primary : '#C62828';
  const evolStr = (pct) => pct === null ? 'N/A' : (parseFloat(pct) >= 0 ? '+'+pct+'%' : pct+'%');

  // Pagination
  const totalPages = Math.max(1, Math.ceil(saisiesFiltrees.length / itemsParPage));
  const saisiesPage = saisiesFiltrees.slice((page-1)*itemsParPage, page*itemsParPage);

  const fmt = (n) => parseFloat(n||0).toLocaleString('fr-FR');
  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR');

  // Styles tableau
  const thGrp = (bg) => ({ background: bg, color: '#fff', padding: '6px 8px', textAlign: 'center',
    fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' });
  const th = { background: '#2E7D32', color: '#FFFFFF', padding: '7px 8px', textAlign: 'center',
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' };
  const td = { padding: '8px 8px', textAlign: 'center', verticalAlign: 'middle',
    fontSize: 12, borderBottom: `1px solid #F0F2F4` };
  const tdL = { ...td, textAlign: 'left' };

  const badge = (s) => ({
    padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    background: s === 'VALIDE' ? '#1B5E20' : '#E8F5E9',
    color: s === 'VALIDE' ? '#FFFFFF' : '#1B5E20',
  });

  const inputF = { padding: '6px 10px', border: `1px solid ${C.border}`, borderRadius: 7,
    fontSize: 12, outline: 'none', background: '#fff' };

  return (
    <div style={{ minHeight: '100vh', background: C.light }}>
      {/* Topbar */}
      <div style={{ height: 52, background: '#fff', borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', position: 'sticky', top: 0, zIndex: 100, flexShrink: 0 }}>
        <div style={{ fontSize: 13, color: C.textLight, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>Production</span>
          <span style={{ color: C.border }}>›</span>
          <span style={{ color: C.textDark, fontWeight: 600 }}>Liste des saisies</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 8,
            padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            onClick={() => navigate('/saisie')}>+ Nouvelle saisie</button>
          <button style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 8,
            padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            onClick={() => {
              const fi = filtreAbattoir ? abattoirs.find(a => a.id === parseInt(filtreAbattoir))?.nom : 'Tous';
              exporterExcel(saisiesFiltrees, totaux, fi + '_' + filtreDebut + '_au_' + filtreFin);
            }}>📥 Excel</button>
          {estAdmin && (
            <button style={{ background: C.primaryDark, color: '#fff', border: 'none', borderRadius: 8,
              padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              onClick={() => navigate('/import')}>📤 Import</button>
          )}
        </div>
      </div>

      <div style={{ padding: '16px 20px', maxWidth: 1600, margin: '0 auto' }}>
        {message.texte && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13,
            background: message.type === 'succes' ? C.green100 : '#FFEBEE',
            color: message.type === 'succes' ? C.primaryDark : '#C62828',
            border: `1px solid ${message.type === 'succes' ? '#A5D6A7' : '#EF9A9A'}` }}>
            {message.texte}
          </div>
        )}

        {/* Filtres compacts */}
        <div style={{ background: '#fff', borderRadius: 12, border: `0.5px solid ${C.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '10px 16px', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {estAdmin && (
              <select style={{ ...inputF, minWidth: 160 }} value={filtreAbattoir}
                onChange={e => { setFiltreAbattoir(e.target.value); setPage(1); }}>
                <option value="">Tous les abattoirs</option>
                {abattoirs.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
              </select>
            )}
            <input style={{ ...inputF, width: 140 }} type="date" value={filtreDebut}
              onChange={e => { setFiltreDebut(e.target.value); setPage(1); }} />
            <span style={{ color: C.textLight, fontSize: 12 }}>→</span>
            <input style={{ ...inputF, width: 140 }} type="date" value={filtreFin}
              onChange={e => { setFiltreFin(e.target.value); setPage(1); }} />
            <button style={{ border: `1px solid ${C.border}`, background: '#fff', borderRadius: 7,
              padding: '6px 12px', color: C.textMid, cursor: 'pointer', fontSize: 12 }}
              onClick={() => { setFiltreAbattoir(''); setFiltreDebut(debutMois); setFiltreFin(finMois); setPage(1); }}>
              Réinitialiser
            </button>
            <span style={{ fontSize: 12, color: C.textLight, marginLeft: 'auto' }}>
              {saisiesFiltrees.length} saisie(s)
              {saisiesN1.length > 0 && <span style={{ marginLeft: 10, color: C.textMid }}>· N-1 : {saisiesN1.length}</span>}
            </span>
          </div>
        </div>

        {/* Tableau */}
        <div style={{ background: '#fff', borderRadius: 12, border: `0.5px solid ${C.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {chargement ? (
            <div style={{ textAlign: 'center', padding: 48, color: C.textLight, fontSize: 14 }}>⏳ Chargement...</div>
          ) : saisiesFiltrees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: C.textLight, fontSize: 14 }}>Aucune saisie pour cette période.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={thGrp('#1B5E20')} rowSpan="2">Date</th>
                      <th style={thGrp('#1B5E20')} rowSpan="2">Abattoir</th>
                      <th style={thGrp('#1B5E20')} colSpan="2">🐄 Bovins nets</th>
                      <th style={thGrp('#1B5E20')} colSpan="2">🐪 Camelins</th>
                      <th style={thGrp('#1B5E20')} colSpan="2">🐑 Ovins nets</th>
                      <th style={thGrp('#1B5E20')} colSpan="2">🐐 Caprins nets</th>
                      <th style={thGrp('#1B5E20')} colSpan="2">🐖 Porcins nets</th>
                      <th style={thGrp('#1B5E20')} colSpan="2">🥩 V.Foraine</th>
                      <th style={thGrp('#1B5E20')} rowSpan="2">⚖️ Poids total</th>
                      <th style={thGrp('#1B5E20')} rowSpan="2">Statut</th>
                      <th style={thGrp('#1B5E20')} rowSpan="2">Actions</th>
                    </tr>
                    <tr>
                      {['Têtes','Poids kg','Têtes','Poids kg','Têtes','Poids kg','Têtes','Poids kg','Têtes','Poids kg','Bov','Autres'].map((h, i) => (
                        <th key={i} style={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {saisiesPage.map(s => {
                      const c = calcSaisie(s);
                      const vf_autres = (parseFloat(s.vf_ovins)||0)+(parseFloat(s.vf_caprins)||0)+(parseFloat(s.vf_porcins)||0);
                      return (
                        <tr key={s.id}
                          onMouseEnter={() => setHoveredRow(s.id)}
                          onMouseLeave={() => setHoveredRow(null)}
                          style={{ background: hoveredRow === s.id ? C.green100 : (s.statut === 'VALIDE' ? C.green100 : '#FFFFFF') }}>
                          <td style={tdL}><strong style={{ color: C.textDark, fontSize: 12 }}>{formatDate(s.date_saisie)}</strong></td>
                          <td style={tdL}><span style={{ fontSize: 12 }}>{s.abattoir_nom}</span></td>
                          <td style={td}>{c.bov_nbr}</td>
                          <td style={{ ...td, color: '#212121' }}>{fmt(c.bov_poids)}</td>
                          <td style={td}>{s.camelins||0}</td>
                          <td style={{ ...td, color: '#212121' }}>{fmt(s.poids_camelins)}</td>
                          <td style={td}>{c.ovi_nbr}</td>
                          <td style={{ ...td, color: '#212121' }}>{fmt(c.ovi_poids)}</td>
                          <td style={td}>{c.cap_nbr}</td>
                          <td style={{ ...td, color: '#212121' }}>{fmt(c.cap_poids)}</td>
                          <td style={td}>{c.por_nbr}</td>
                          <td style={{ ...td, color: '#212121' }}>{fmt(c.por_poids)}</td>
                          <td style={{ ...td, color: '#212121' }}>{fmt(s.vf_bovins)}</td>
                          <td style={{ ...td, color: '#212121' }}>{fmt(vf_autres)}</td>
                          <td style={{ ...td, fontWeight: 700, color: '#212121' }}>{fmt(c.poids_total)}</td>
                          <td style={td}><span style={badge(s.statut)}>{s.statut === 'VALIDE' ? '✅ VALIDÉ' : '⏳ BROUILLON'}</span></td>
                          <td style={td}>
                            {s.statut === 'BROUILLON' && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <button style={{ background: '#fff', color: '#1B5E20', border: `1px solid #1B5E20`,
                                  borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                                  onClick={() => navigate(`/saisies/${s.id}/modifier`)}>✏️ Modifier</button>
                                <button style={{ background: C.primary, color: '#fff', border: 'none',
                                  borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                                  onClick={() => valider(s.id)}>🔒 Valider</button>
                              </div>
                            )}
                            {s.statut === 'VALIDE' && estAdmin && (
                              <button style={{ background: '#C62828', color: '#fff', border: 'none',
                                borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                                onClick={() => navigate(`/saisies/${s.id}/modifier`)}>🔑 Corriger</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Ligne TOTAUX */}
                    <tr style={{ background: '#E8F5E9', fontWeight: 700, borderTop: `2px solid ${C.border}` }}>
                      <td style={{ ...tdL, color: '#212121', fontSize: 11 }} colSpan="2">
                        📊 TOTAUX ({saisiesFiltrees.length} saisies)
                      </td>
                      <td style={{ ...td, fontWeight: 700 }}>{totaux.bov_nbr}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmt(totaux.bov_poids)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{totaux.cam_nbr}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmt(totaux.cam_poids)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{totaux.ovi_nbr}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmt(totaux.ovi_poids)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{totaux.cap_nbr}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmt(totaux.cap_poids)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{totaux.por_nbr}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmt(totaux.por_poids)}</td>
                      <td style={td} colSpan="2"></td>
                      <td style={{ ...td, fontWeight: 800, color: '#212121', fontSize: 13 }}>{fmt(totaux.poids_total)}</td>
                      <td style={td} colSpan="2"></td>
                    </tr>

                    {/* Ligne N-1 */}
                    {saisiesN1.length > 0 && (
                      <tr style={{ background: '#FFFFFF', borderTop: `1px dashed ${C.border}` }}>
                        <td style={{ ...tdL, color: '#212121', fontSize: 11 }} colSpan="2">📅 N-1 ({saisiesN1.length} saisies)</td>
                        {[totauxN1.bov_nbr, totauxN1.bov_poids, totauxN1.cam_nbr, totauxN1.cam_poids,
                          totauxN1.ovi_nbr, totauxN1.ovi_poids, totauxN1.cap_nbr, totauxN1.cap_poids,
                          totauxN1.por_nbr, totauxN1.por_poids].map((v, i) => (
                          <td key={i} style={{ ...td, color: '#212121' }}>{i % 2 === 0 ? v : fmt(v)}</td>
                        ))}
                        <td style={td} colSpan="2"></td>
                        <td style={{ ...td, color: '#212121' }}>{fmt(totauxN1.poids_total)}</td>
                        <td style={td} colSpan="2"></td>
                      </tr>
                    )}

                    {/* Ligne évolution % */}
                    {saisiesN1.length > 0 && (
                      <tr style={{ background: '#FFFFFF', borderTop: `1px solid ${C.border}` }}>
                        <td style={{ ...tdL, color: '#212121', fontSize: 11, fontWeight: 700 }} colSpan="2">📈 Évolution N/N-1</td>
                        {[
                          [totaux.bov_nbr, totauxN1.bov_nbr], [totaux.bov_poids, totauxN1.bov_poids],
                          [totaux.cam_nbr, totauxN1.cam_nbr], [totaux.cam_poids, totauxN1.cam_poids],
                          [totaux.ovi_nbr, totauxN1.ovi_nbr], [totaux.ovi_poids, totauxN1.ovi_poids],
                          [totaux.cap_nbr, totauxN1.cap_nbr], [totaux.cap_poids, totauxN1.cap_poids],
                          [totaux.por_nbr, totauxN1.por_nbr], [totaux.por_poids, totauxN1.por_poids],
                        ].map(([v, v1], i) => {
                          const pct = evol(v, v1);
                          return <td key={i} style={td}><strong style={{ color: evolColor(pct), fontSize: 11 }}>{evolStr(pct)}</strong></td>;
                        })}
                        <td style={td} colSpan="2"></td>
                        <td style={td}>
                          <strong style={{ color: evolColor(evol(totaux.poids_total, totauxN1.poids_total)), fontSize: 12 }}>
                            {evolStr(evol(totaux.poids_total, totauxN1.poids_total))}
                          </strong>
                        </td>
                        <td style={td} colSpan="2"></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Odoo style */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 16px', borderTop: `1px solid ${C.border}`,
                background: '#FAFAFA', borderRadius: '0 0 12px 12px' }}>
                <div style={{ fontSize: 12, color: C.textMid }}>
                  {saisiesFiltrees.length} saisie(s) · Page {page} sur {totalPages}
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  {[['«', () => setPage(1), page === 1],
                    ['‹', () => setPage(p => Math.max(1,p-1)), page === 1]].map(([lbl, fn, dis], i) => (
                    <button key={i} onClick={fn} disabled={dis} style={{
                      width: 30, height: 30, border: `1px solid ${C.border}`, borderRadius: 6,
                      background: dis ? '#F5F5F5' : '#fff', color: dis ? '#BDBDBD' : C.textMid,
                      cursor: dis ? 'default' : 'pointer', fontSize: 13,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{lbl}</button>
                  ))}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx-1] > 1) acc.push('...');
                      acc.push(p); return acc;
                    }, [])
                    .map((p, idx) => p === '...' ? (
                      <span key={`sep-${idx}`} style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: C.textLight }}>…</span>
                    ) : (
                      <button key={`pg-${p}`} onClick={() => setPage(p)} style={{
                        width: 30, height: 30, border: page === p ? 'none' : `1px solid ${C.border}`,
                        borderRadius: 6, background: page === p ? C.primary : '#fff',
                        color: page === p ? '#fff' : C.textMid, fontWeight: page === p ? 700 : 400,
                        cursor: 'pointer', fontSize: 12,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p}</button>
                    ))
                  }
                  {[['›', () => setPage(p => Math.min(totalPages,p+1)), page === totalPages],
                    ['»', () => setPage(totalPages), page === totalPages]].map(([lbl, fn, dis], i) => (
                    <button key={i} onClick={fn} disabled={dis} style={{
                      width: 30, height: 30, border: `1px solid ${C.border}`, borderRadius: 6,
                      background: dis ? '#F5F5F5' : '#fff', color: dis ? '#BDBDBD' : C.textMid,
                      cursor: dis ? 'default' : 'pointer', fontSize: 13,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{lbl}</button>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, color: C.textMid }}>Lignes :</span>
                  <select value={itemsParPage} onChange={e => { setItemsParPage(Number(e.target.value)); setPage(1); }}
                    style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 8px', fontSize: 12, color: C.textMid, background: '#fff', cursor: 'pointer' }}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
